import axios from "../axios";

const getAuthHeaders = () => {
  const authToken = localStorage.getItem("access-Token-Edify");
  if (!authToken) return {};
  return { headers: { Authorization: `Bearer ${authToken}` } };
};

export type ProfessionalResponse = {
  id: number | string;
  type?: "PERSON" | "COMPANY";
  name?: string;
  last_name?: string;
  razon_social?: string;
  validated?: boolean;
  username?: string;
  url_site_web?: string;
  zone?: string;
  image_profile?: string;
  phone?: string;
  skill?: Array<{ skill: { id?: number; name: string } }>;
  skills?: Array<{ id?: number; name: string }>;
  portfolio?: Array<{
    id?: number | string;
    title?: string;
    portfolio_images?: Array<{ image: string }>;
  }>;
  stars?: number;
};

export type SkillResponse = {
  id: number;
  name: string;
  users?: number;
};

export type SkillCatalogItem = {
  id: number;
  name: string;
};

export type JurisdictionResponse = {
  id: number;
  name: string;
  userCount?: number;
  allCountryCount?: number;
};

export type FilterParams = {
  skillIds?: number[];
  jurisdictionIds?: number[];
  allCountry?: boolean;
  order?: "ASC" | "DESC";
  page?: number;
  pageSize?: number;
  type?: "PERSON" | "COMPANY";
};

export type FilterResponse = {
  users: ProfessionalResponse[];
  itemCount: number;
  page: number;
  take: number;
};

const normalizeSkillName = (name: string) =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const sanitizeProfessionals = (professionals: ProfessionalResponse[]) =>
  professionals.filter((professional) => {
    if (!professional.skill || !Array.isArray(professional.skill)) return true;
    return !professional.skill.some((item) => {
      const skillName = item.skill?.name;
      return skillName ? normalizeSkillName(skillName) === "otros" : false;
    });
  });

export const extractHighlightedUsers = (professionals: ProfessionalResponse[], take = 12) => {
  const prioritized = [...professionals].sort((a, b) => {
    const verifiedA = a.validated ? 1 : 0;
    const verifiedB = b.validated ? 1 : 0;
    if (verifiedB !== verifiedA) return verifiedB - verifiedA;
    const starsA = a.stars ?? 0;
    const starsB = b.stars ?? 0;
    if (starsB !== starsA) return starsB - starsA;
    const portfolioA = a.portfolio?.length ?? 0;
    const portfolioB = b.portfolio?.length ?? 0;
    return portfolioB - portfolioA;
  });
  return prioritized.slice(0, take);
};

export const sortByPortfolio = (professionals: ProfessionalResponse[]) =>
  [...professionals].sort((a, b) => {
    const portfolioA = a.portfolio?.length ?? 0;
    const portfolioB = b.portfolio?.length ?? 0;
    if (portfolioA === portfolioB) return 0;
    return portfolioB - portfolioA;
  });

const normalizeSkills = (skills: SkillResponse[]) =>
  [...skills].sort((a, b) => a.name.localeCompare(b.name));

const normalizeJurisdictions = (jurisdictions: JurisdictionResponse[]) =>
  [...jurisdictions].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));

const normalizeFilterResponse = (response: FilterResponse) => ({
  users: response.users ?? [],
  itemCount: response.itemCount ?? 0,
  page: response.page ?? 1,
  take: response.take ?? 12,
});

const buildQueryParams = (params: FilterParams) => {
  const searchParams = new URLSearchParams();
  params.skillIds?.forEach((id) => searchParams.append("skillIds", String(id)));
  params.jurisdictionIds?.forEach((id) => searchParams.append("jurisdictionIds", String(id)));
  if (params.allCountry) searchParams.append("allCountry", "true");
  if (params.type) searchParams.append("type", params.type);
  searchParams.append("order", params.order ?? "ASC");
  searchParams.append("page", String(params.page ?? 1));
  searchParams.append("take", String(params.pageSize ?? 12));
  return searchParams.toString();
};

export const profesionalesService = {
  fetchRegisteredUsers: async () => {
    const { data } = await axios.get<ProfessionalResponse[]>("/user/registers", getAuthHeaders());
    return sanitizeProfessionals(data ?? []);
  },
  fetchSkills: async () => {
    const { data } = await axios.get<{ response: { results: SkillResponse[] } }>("/skill/AllUsers", getAuthHeaders());
    const skills = data?.response?.results ?? [];
    return normalizeSkills(skills);
  },
  fetchJurisdictions: async () => {
    const { data } = await axios.get<JurisdictionResponse[]>("/user/count/jurisdiction", getAuthHeaders());
    return normalizeJurisdictions(data ?? []);
  },
  fetchSkillCatalog: async () => {
    const { data } = await axios.get<SkillCatalogItem[]>("/skill/list", getAuthHeaders());
    return (data ?? []).map((item) => ({
      id: Number(item.id),
      name: String(item.name ?? "").trim(),
    }));
  },
  fetchFilteredProfessionals: async (params: FilterParams) => {
    const query = buildQueryParams(params);
    const { data } = await axios.get<FilterResponse>(`/user/filter?${query}`, getAuthHeaders());
    const normalized = normalizeFilterResponse(data ?? { users: [], itemCount: 0, page: 1, take: params.pageSize ?? 12 });
    return {
      users: sanitizeProfessionals(normalized.users),
      itemCount: normalized.itemCount,
      page: normalized.page,
      take: normalized.take,
    };
  },
};
